import {
  format,
  parse,
  parseISO,
  isValid,
  addDays,
  subDays,
  isEqual,
  isSameDay,
  isSameMonth,
  getDay,
} from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import queryString from 'query-string'

import { FlexibleImageSrc } from '../components/types'

export const checkStringPeriod = (str: string) => {
  return str.substr(-1) === '.' ? str : `${str}.`
}

export const checkStringQuestionMark = (str: string) => {
  return str.substr(-1) === '?' ? str : `${str}?`
}

export const parseImageSrc = (
  imageSrc: FlexibleImageSrc,
): { src: string; srcSet: string } => {
  if (typeof imageSrc === 'string') {
    return { src: imageSrc, srcSet: '' }
  } else {
    const {
      hero,
      heroWidth,
      heroLarge,
      heroLargeWidth,
      large,
      largeWidth,
      medium,
      mediumWidth,
      small,
      smallWidth,
    } = imageSrc
    const sources = [
      `${small} ${smallWidth}w`,
      `${medium} ${mediumWidth}w`,
      `${large} ${largeWidth}w`,
    ]

    if (hero && heroWidth) {
      sources.push(`${hero} ${heroWidth}w`)
    }

    if (heroLarge && heroLargeWidth) {
      sources.push(`${heroLarge} ${heroLargeWidth}w`)
    }

    const srcSet = sources.join(', ')
    const src = large
    return { src, srcSet }
  }
}

// Translates '2020-03-16 09:09' to '2020-03-16T09:09Z'
// Makes timezone UTC
export const formatISOStringToUTC = (date: string): string => {
  const timestamp = date.replace(/\s/, 'T')
  return `${timestamp}Z`
}

export const formatDate = (
  date: string | number | Date,
  formatter: string,
): string => {
  switch (typeof date) {
    case 'number':
      if (date < 28800000) {
        return date.toString().length < 8
          ? ''
          : format(parseDateString(date.toString()), formatter)
      }

      if (isValid(date)) {
        return format(date, formatter)
      }

      return ''

    case 'string':
      if (date.toString().length < 8) {
        return ''
      }

      if (isValid(parseISO(date))) {
        return format(parseISO(date), formatter)
      }

      if (isValid(parseDateString(date))) {
        return format(parseDateString(date), formatter)
      }

      return ''

    default:
      return isValid(date) ? format(date, formatter) : ''
  }
}

export const formatStartEndDate = (
  startDate: Date,
  endDate: Date,
  includeYear = false,
): string => {
  if (isSameDay(endDate, startDate)) {
    return includeYear
      ? format(startDate, 'MMM d, yyyy')
      : format(startDate, 'MMM d')
  } else if (isSameMonth(endDate, startDate)) {
    return includeYear
      ? `${format(startDate, 'MMM d')}-${format(endDate, 'd, yyyy')}`
      : `${format(startDate, 'MMM d')}-${format(endDate, 'd')}`
  } else {
    return includeYear
      ? `${format(startDate, 'MMM d')}-${format(endDate, 'MMM d, yyyy')}`
      : `${format(startDate, 'MMM d')}-${format(endDate, 'MMM d')}`
  }
}

export const parseDateString = (date: string): Date => {
  return parse(
    date
      .replace(/-/g, '')
      .substr(-6)
      .replace(/^\//, '')
      .replace(/(\d{2})(\d{2})(\d{2})/, '20$1-$2-$3'),
    'yyyy-MM-dd',
    new Date(),
  )
}

export const formatDailyDate = (date: Date): string => {
  return format(date, 'yyMMdd')
}

export const nextDay = (date: Date): Date => {
  return addDays(date, 1)
}

export const previousDay = (date: Date): Date => {
  return subDays(date, 1)
}

export const differentDate = (a: Date, b: Date): boolean => {
  return !isEqual(a, b)
}

export const differentDayStrings = (a: string, b: string): boolean => {
  return !isSameDay(parseISO(a), parseISO(b))
}

export const dayOfWeek = (date: Date): number => {
  return getDay(date)
}

export const isValidDate = isValid

export const cleanUpPath = (path: string, commentsPath: string): string => {
  const commentsPathMatch = RegExp(`${commentsPath}/?$`, 'gi')
  return path.replace(commentsPathMatch, '').replace(/(^.+)\/$/, '$1')
}

export const getPacificTimezone = () =>
  zonedTimeToUtc(new Date(), 'America/Los_Angeles')

export const firstWODDate = new Date(Date.UTC(2001, 1, 10))

/**
 * Detect if the App is pre-rendering, use this to avoid unnecessary api calls or steps during the pre-render
 */
export const isPreRendering = (): boolean => {
  if (navigator && navigator.userAgent) {
    return navigator.userAgent === 'CFPreRender'
  }
  return false
}

export const generateCommentsFullPathUrl = ({
  fullPath,
  topicId,
}: {
  fullPath?: string
  topicId: string
}): string => {
  const path = fullPath || ''
  return `${path}?topicId=${topicId}`
}

/**
 * Args in urls can be string or arrays of strings. We'll never use arrays.
 */
export const apiArgIsNotArray = (
  arg: string | string[] | number | number[],
): string | number => (Array.isArray(arg) ? arg[0] : arg)

export const apiArgIsString = (arg: string | (string | number)[]): string =>
  Array.isArray(arg) ? arg[0].toString() : arg

export const apiArgIsNumber = (arg: number | number[]): number =>
  Array.isArray(arg) ? arg[0] : arg

export const getQueryVariable = (
  variable: string,
): string | number | undefined | null | string[] => {
  const parsedParams = queryString.parse(window.location.search)
  return parsedParams[variable]
}
